import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ShoppingBag, Star, BookOpen, ImageIcon, Search, X } from "lucide-react";
import { RequestProduct } from "@/components/RequestProduct";
import { api } from "@/lib/api";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { Seo } from "@/lib/seo";

const AffiliateCard = ({ rec }) => {
  const openLink = (e) => {
    // Fire a non-blocking click beacon; the link still opens normally.
    api.post(`/shop-affiliates/${rec.id}/click`).catch(() => {});
  };
  const retailerLabel = rec.retailer || "retailer";
  return (
    <article data-testid={`affiliate-card-${rec.id}`} className="bg-white rounded-xl border border-navy/15 overflow-hidden flex flex-col group">
      <div className="aspect-[4/3] bg-cream flex items-center justify-center overflow-hidden border-b border-navy/10">
        {(rec.amazon_image_link || rec.image_url) ? (
          <a href={rec.affiliate_url} target="_blank" rel="sponsored noopener noreferrer" onClick={openLink} className="w-full h-full flex items-center justify-center bg-white">
            <img src={rec.amazon_image_link || rec.image_url} alt={rec.title} loading="lazy" className="max-w-full max-h-full object-contain" referrerPolicy="no-referrer" />
          </a>
        ) : (
          <a href={rec.affiliate_url} target="_blank" rel="sponsored noopener noreferrer" onClick={openLink} className="flex flex-col items-center text-navy/45 hover:text-brick transition-colors">
            <ExternalLink className="w-9 h-9" />
            <span className="text-xs mt-2 font-bold uppercase tracking-wide">View product on {retailerLabel}</span>
          </a>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          {rec.retailer && (
            <span data-testid={`affiliate-retailer-${rec.id}`} className="text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 bg-navy/10 text-navy">
              {rec.retailer}
            </span>
          )}
          {rec.price_note && <span className="text-sm font-bold text-brick">{rec.price_note}</span>}
        </div>
        <h3 className="mt-3 font-display text-xl text-navy leading-tight">{rec.title}</h3>
        {rec.recommendation && (
          <p className="mt-2 text-sm text-ink/75 leading-relaxed flex-1">
            <span className="inline-flex items-center gap-1 font-bold text-navy"><Star className="w-3.5 h-3.5 fill-gold text-gold" /> Johnny says:</span>{" "}
            {rec.recommendation}
          </p>
        )}
        <a
          href={rec.affiliate_url}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={openLink}
          data-testid={`affiliate-link-${rec.id}`}
          className="mt-4 inline-flex items-center justify-center gap-2 bg-brick text-white font-bold uppercase text-sm rounded-md px-4 py-3 hover:bg-navy transition-colors"
        >
          View on {retailerLabel} <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </article>
  );
};

export default function ShopAffiliates() {
  const [recs, setRecs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shop-affiliates/categories").then((r) => setCategories(["All", ...r.data])).catch(() => {});
    api.get("/shop-affiliates")
      .then((r) => setRecs(r.data))
      .catch(() => setRecs([]))
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const matchesQuery = (r) =>
    [r.title, r.recommendation, r.category, r.retailer, r.price_note]
      .some((f) => (f || "").toLowerCase().includes(q));
  // When searching, look across ALL products (ignore category). Otherwise filter by category.
  const visible = recs.filter((r) =>
    searching ? matchesQuery(r) : (category === "All" || r.category === category)
  );

  return (
    <div data-testid="shop-affiliates-page">
      <Seo title="Gorilla Gateway — Amazon Product Picks" path="/gateway" description="Shop Johnny's favorite Amazon and retailer product picks through the Gorilla Gateway. Search by name to find exactly what you need. As an Amazon Associate, JohnnyMart earns from qualifying purchases." />
      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #f2b134 0, transparent 40%), radial-gradient(circle at 80% 0, #b5231d 0, transparent 35%)" }} />
        <div className="jm-container relative py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-gold text-xs font-bold uppercase tracking-[0.2em]">
            <ShoppingBag className="w-4 h-4" /> Affiliate Shopping
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05] max-w-3xl">
            Enter the <span className="text-gold">Gorilla Gateway</span>
          </h1>
          <p className="mt-5 text-cream/75 text-base md:text-lg max-w-2xl leading-relaxed">
            Shop at retailers you already know and trust through JohnnyMart's affiliate links. You still
            complete your purchase directly with the retailer at its normal price. When you enter through
            the Gorilla Gateway, JohnnyMart may earn a commission from qualifying purchases at no
            additional cost to you.
          </p>

          <div className="mt-7 max-w-xl relative">
            <Search className="w-5 h-5 text-navy/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="gateway-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Amazon products by name…"
              className="w-full rounded-full bg-cream text-navy placeholder:text-navy/45 pl-12 pr-11 py-3.5 text-base font-medium outline-none focus:ring-2 focus:ring-gold shadow-lg"
            />
            {query && (
              <button
                data-testid="gateway-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/50 hover:text-brick p-1"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="jm-container py-10">
        <RequestProduct />

        <AffiliateDisclosure />

        {searching ? (
          <h2 className="mt-8 font-display text-3xl text-navy" data-testid="gateway-search-heading">
            {visible.length} result{visible.length === 1 ? "" : "s"} for “{query.trim()}”
          </h2>
        ) : (
          <h2 className="mt-8 font-display text-3xl text-navy" data-testid="gateway-products-heading">Amazon Products</h2>
        )}

        {!searching && categories.length > 1 && (
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`affiliate-cat-${c}`}
                onClick={() => setCategory(c)}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${category === c ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy hover:border-brick"}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Link to="/shop-affiliates/guides" data-testid="guides-link" className="inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-brick">
            <BookOpen className="w-4 h-4" /> Buying Guides
          </Link>
        </div>

        {loading ? (
          <p className="mt-12 text-center text-navy/50">Loading recommendations...</p>
        ) : visible.length === 0 ? (
          searching ? (
            <div data-testid="gateway-search-empty" className="mt-12 text-center bg-white rounded-xl border border-navy/15 p-12">
              <Search className="w-12 h-12 text-navy/20 mx-auto" />
              <p className="font-display text-2xl text-navy mt-4">No match for “{query.trim()}”</p>
              <p className="text-ink/60 mt-2">We don't carry that one yet. Try a different name or keyword, or browse all products.</p>
              <button data-testid="gateway-search-reset" onClick={() => setQuery("")} className="mt-5 inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3 hover:bg-navy transition-colors">
                Browse all products
              </button>
            </div>
          ) : (
            <div data-testid="affiliates-empty" className="mt-12 text-center bg-white rounded-xl border border-navy/15 p-12">
              <ShoppingBag className="w-12 h-12 text-navy/20 mx-auto" />
              <p className="font-display text-2xl text-navy mt-4">More must-buys coming soon</p>
              <p className="text-ink/60 mt-2">Johnny is curating his favorite finds. Check back shortly.</p>
            </div>
          )
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((r) => <AffiliateCard key={r.id} rec={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}
